// @flow
import { forEach } from 'lodash'
import { css, cssFor } from 'glamor'
import type { CSS } from 'glamorous'

export const BASE_FONT_SIZE = 16

export const toFixed = (number: number, precision: number): number => {
  const multiplier = 10 ** (precision + 1)
  const wholeNumber = Math.floor(number * multiplier)
  return (Math.round(wholeNumber / 10) * 10) / multiplier
}

export const pxToRem = (px: number): number | string => {
  if (px < 0) return px

  return `${toFixed(px / BASE_FONT_SIZE, 5)}rem`
}

export const convertHexToRGB = (color: string) => {
  let convertedColor = color
  if (color.length === 4) {
    let extendedColor = '#'
    for (let i = 1; i < color.length; i += 1) {
      extendedColor += color.charAt(i) + color.charAt(i)
    }
    convertedColor = extendedColor
  }

  const values = {
    r: parseInt(convertedColor.substr(1, 2), 16),
    g: parseInt(convertedColor.substr(3, 2), 16),
    b: parseInt(convertedColor.substr(5, 2), 16),
  }

  return `rgb(${values.r}, ${values.g}, ${values.b})`
}

export const decomposeColor = (color: string) => {
  if (color.charAt(0) === '#') {
    return decomposeColor(convertHexToRGB(color))
  }

  const marker = color.indexOf('(')
  const type = color.substring(0, marker)
  let values = color.substring(marker + 1, color.length - 1).split(',')
  values = values.map(value => parseFloat(value))

  return { type, values }
}

export const getLuminance = (color: string) => {
  const decomposedColor = decomposeColor(color)

  if (decomposedColor.type.indexOf('rgb') > -1) {
    const rgb = decomposedColor.values.map(val => {
      const normalizedVal = val / 255 // normalized
      return normalizedVal <= 0.03928
        ? normalizedVal / 12.92
        : (normalizedVal + 0.055) / 1.055 ** 2.4
    })
    // Truncate at 3 digits
    return Number(
      (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]).toFixed(3),
    )
  }

  if (decomposedColor.type.indexOf('hsl') > -1) {
    return decomposedColor.values[2] / 100
  }

  return 0
}

export const getContrastRatio = (foreground: string, background: string) => {
  const lumA = getLuminance(foreground)
  const lumB = getLuminance(background)
  const contrastRatio =
    (Math.max(lumA, lumB) + 0.05) / (Math.min(lumA, lumB) + 0.05)

  return Number(contrastRatio.toFixed(2)) // Truncate at two digits
}

export const colorWithOpacity = (color: string, opacity: number) => {
  const { values, type } = decomposeColor(color)

  if (type.indexOf('rgb') > -1) {
    return `rgba(${values[0]}, ${values[1]}, ${values[2]}, ${opacity})`
  }

  if (type.indexOf('hsl') > -1) {
    return `hsla(${values[0]}, ${values[1]}, ${values[2]}, ${opacity})`
  }

  return color
}

export const positionCenter = () => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
})

export const assitiveText = {
  position: 'absolute',
  margin: -1,
  border: 0,
  padding: 0,
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  textTransform: 'none',
  whiteSpace: 'nowrap',
}

export const globalWithMediaQueries = (selector: string, cssStyles: CSS) => {
  const plainStyles = {}
  let media
  forEach(cssStyles, (cssStyle, property) => {
    if (property.includes('@media')) {
      const mediaCss = cssFor(cssStyle)
      media = `${property}{${selector}${mediaCss.replace(/^.+(?={)/, '')}}`
    } else {
      plainStyles[property] = cssStyle
    }
  })
  css.global(selector, plainStyles)
  if (media) {
    css.insert(media)
  }
}



// WEBPACK FOOTER //
// ./src/style/util.js